import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Slide } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  addMultipleElements,
  removeMultipleElements,
  updateLayout,
  updateMultiSelection,
  updateMultiUpload,
  updateSelectedTile,
  updatePreviousTiles,
  updateIsUserAdding,
  updateIsUserDeleting,
  generateMyGameLayer,
} from "../../../reducers/configurator/configuratorSlice";
import TileDialogActions from "./TileDialogActions";
import TileDialogContent from "./TileDialogContent";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { resetLayers } from "../../../reducers/configurator/utils";
import ConfiguratorIcons from "../ConfiguratorIcons";
import MultiUploadDialogContent from "../generalOptions/MultiUploadDialogContent";
import { gamesSizesHelper } from "../utils/utils";

const PREFIX = "TileDialog";

const classes = {
  root: `${PREFIX}-root`,
  closeIconButton: `${PREFIX}-closeIconButton`,
  mainContainer: `${PREFIX}-mainContainer`,
  multiSelectionContainer: `${PREFIX}-multiSelectionContainer`,
  buttonsContainer: `${PREFIX}-buttonsContainer`,
  buttonsContainerCentered: `${PREFIX}-buttonsContainerCentered`,
  contentContainer: `${PREFIX}-contentContainer`,
  confirmationButton: `${PREFIX}-confirmationButton`,
  textStyle: `${PREFIX}-textStyle`,
};

const TileDialogContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      width: "100%",
      // minHeight: "270px",
      height: "350px",
      overflow: "auto",
      position: "fixed",
      zIndex: 1200,
      bottom: "0",
      backgroundColor: "#303030",
      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",
      borderTop: `1px solid ${theme.palette.primary.main}`,
      padding: "16px",
      display: "flex",
      flexDirection: "row",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
    [`& .${classes.closeIconButton}`]: {
      position: "absolute",
      right: "0",
      top: "0",
      zIndex: 999,
    },
    [`& .${classes.mainContainer}`]: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      maxWidth: "650px",
      margin: "0 auto",
    },
    [`& .${classes.multiSelectionContainer}`]: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "space-evenly",
      width: "100%",
      height: "200px",
    },
    [`& .${classes.buttonsContainer}`]: {
      height: "20%",
      display: "flex",
      marginBottom: "10px",
      width: "100%",
      paddingRight: "1.5rem",
    },
    [`& .${classes.buttonsContainerCentered}`]: {
      height: "20%",
      display: "flex",
      justifyContent: "center",
      marginBottom: "10px",
      width: "100%",
      paddingRight: "1.5rem",
    },
    [`& .${classes.confirmationButton}`]: {
      fontSize: "0.875rem",
      marginBottom: "1rem",
    },
    [`& .${classes.textStyle}`]: {
      margin: "0",
      fontWeight: "400",
      fontSize: "1rem",
      lineHeight: "1.5",
      color: "rgba(255, 255, 255, 0.7)",
    },
    [`& .${classes.contentContainer}`]: {
      height: "100%",
      width: "100%",
    },
  };
});

export const TileDialog = ({ currentTile, configObjects }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const multiSelection = useSelector(
    (state) => state.configurator.multiSelection
  );
  const layout = useSelector((state) => state.configurator.layout);
  const isMultiUpload = useSelector(
    (state) => state.configurator.multiUpload.active
  );
  const grid = useSelector((state) => state.configurator.grid);

  const handleClick = () => {
    if (multiSelection.isDelete) {
      dispatch(
        removeMultipleElements({
          layer: layout === "normal" ? multiSelection.layer : layout,
          tiles: multiSelection.tile,
        })
      );
      dispatch(updateMultiSelection(resetLayers.multiSelection));
      dispatch(updateIsUserAdding(false));
      dispatch(updateIsUserDeleting(false));
      dispatch(updatePreviousTiles([]));
    } else {
      // games and generation
      if (
        multiSelection.data.type.includes("basket") &&
        !grid.hasOwnProperty("basket")
      ) {
        dispatch(
          generateMyGameLayer({
            gameName: "basket",
            row: gamesSizesHelper.basket.row,
            column: gamesSizesHelper.basket.column,
          })
        );
      } else if (
        multiSelection.data.type.includes("football") &&
        !grid.hasOwnProperty("football")
      ) {
        dispatch(
          generateMyGameLayer({
            gameName: "football",
            row: gamesSizesHelper.football.row,
            column: gamesSizesHelper.football.column,
          })
        );
      } else if (
        multiSelection.data.type.includes("arcade") &&
        !grid.hasOwnProperty("arcade")
      ) {
        dispatch(
          generateMyGameLayer({
            gameName: "arcade",
            row: gamesSizesHelper.arcade.row,
            column: gamesSizesHelper.arcade.column,
          })
        );
      } else if (
        multiSelection.data.type.includes("pickleball") &&
        !grid.hasOwnProperty("pickleball")
      ) {
        dispatch(
          generateMyGameLayer({
            gameName: "pickleball",
            row: gamesSizesHelper.pickleball.row,
            column: gamesSizesHelper.pickleball.column,
          })
        );
      }
      dispatch(
        addMultipleElements({
          layer: layout === "normal" ? multiSelection.layer : layout,
          tiles: multiSelection.tile,
          data: multiSelection.data,
        })
      );
      dispatch(updateMultiSelection(resetLayers.multiSelection));
      dispatch(updateIsUserAdding(false));
      dispatch(updateIsUserDeleting(false));
      dispatch(updatePreviousTiles([]));
    }
  };

  const handleCloseSlide = () => {
    if (isMultiUpload) {
      dispatch(updateMultiUpload(resetLayers.multiUpload));
    } else if (layout === "walls") {
      dispatch(updateLayout("normal"));
      dispatch(updatePreviousTiles([]));
      dispatch(updateIsUserAdding(false));
      dispatch(updateIsUserDeleting(false));
    } else {
      dispatch(updateMultiSelection(resetLayers.multiSelection));
      dispatch(updateSelectedTile(null));
      dispatch(updatePreviousTiles([]));
      dispatch(updateIsUserAdding(false));
      dispatch(updateIsUserDeleting(false));
    }
  };

  return (
    <TileDialogContainer>
      <Slide
        direction="up"
        in={layout === "walls" || currentTile || isMultiUpload ? true : false}
        mountOnEnter
        unmountOnExit
      >
        {currentTile || isMultiUpload || layout === "walls" ? (
          <div className={classes.root}>
            <IconButton
              color="primary"
              aria-label="close tile details"
              className={classes.closeIconButton}
              onClick={() => handleCloseSlide()}
            >
              <CloseIcon />
            </IconButton>
            {isMultiUpload ? (
              <MultiUploadDialogContent />
            ) : (
              <div className={classes.mainContainer}>
                {multiSelection.active ? (
                  <div>
                    {(multiSelection.unlimited ||
                      multiSelection.data?.type === "spawn") && (
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.confirmationButton}
                        onClick={() => handleClick()}
                      >
                        {t("configurator.confirmSelection")}
                      </Button>
                    )}
                    {multiSelection.isDelete ? (
                      <div className={classes.multiSelectionContainer}>
                        <p className={classes.textStyle}>
                          {t(`configurator.selectDelete${layout}Tiles`)}
                        </p>
                      </div>
                    ) : multiSelection.move ? (
                      <div className={classes.multiSelectionContainer}>
                        <p className={classes.textStyle}>
                          {t(`configurator.selectTileMoveGeneral`)}
                        </p>
                        <p className={classes.textStyle}>
                          {multiSelection.data.type}
                        </p>
                        <ConfiguratorIcons
                          orientation={multiSelection.data}
                          side={"neutralPosition"}
                        />
                      </div>
                    ) : !multiSelection.unlimited ? (
                      <div className={classes.multiSelectionContainer}>
                        <p className={classes.textStyle}>
                          {multiSelection.data.type !== "frame"
                            ? t(`configurator.selectEndObject`)
                            : t(`configurator.selectEndCustomFrame`)}
                        </p>
                      </div>
                    ) : (
                      <div className={classes.multiSelectionContainer}>
                        <p className={classes.textStyle}>
                          {t("configurator.selectWantedTiles")}
                        </p>
                        <p className={classes.textStyle}>
                          {multiSelection.data.type}
                        </p>
                        <ConfiguratorIcons
                          orientation={multiSelection.data}
                          side={"neutralPosition"}
                        />
                        <p className={classes.textStyle}>
                          {t("configurator.thenPressConfirm")}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div
                      className={
                        layout === "walls"
                          ? classes.buttonsContainerCentered
                          : classes.buttonsContainer
                      }
                    >
                      <TileDialogActions configObjects={configObjects} />
                    </div>
                    <div className={classes.contentContainer}>
                      <TileDialogContent />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <div />
        )}
      </Slide>
    </TileDialogContainer>
  );
};
